  import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import Toast from "../../Toast";
import Loader from "../../Loader";

const pagesApiUrl = process.env.REACT_APP_PAGES_API_URL;

const WebsitePage = ({ pages = [] }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processedContent, setProcessedContent] = useState("");
  const contentRef = useRef(null);

  // Function to process HTML content and convert backend URLs to React Router paths
  const processContent = (htmlContent) => {
    if (!htmlContent) return "";
    
    // Replace backend URLs with React Router paths
    let processed = htmlContent;
    
    // Replace full backend URLs (https://ppimpl.com/pages/slug)
    processed = processed.replace(
      /https?:\/\/(www\.)?ppimpl\.com\/pages\/([^"'\s<>]+)/gi,
      '/pages/$2'
    );
    
    // Replace relative backend URLs that might be absolute from root
    processed = processed.replace(
      /href=["']\/pages\/([^"']+)["']/gi,
      'href="/pages/$1" data-react-router-link="true"'
    );
    
    return processed;
  };

  useEffect(() => {
    const localMatch = pages?.find((item) => item?.slug === slug);
    if (localMatch) {
      setPage(localMatch);
      setProcessedContent(processContent(localMatch?.content || ""));
      return;
    }

    const fetchPage = async () => {
      try {
        setIsLoading(true);
        const response = await axios({
          method: "get",
          url: pagesApiUrl,
          headers: {
            Accept: "application/json",
          },
        });

        if (response.status === 200 && response.data?.success) {
          const list = response.data?.data || [];
          const match = list.find((item) => item?.slug === slug);
          if (match) {
            setPage(match);
            setProcessedContent(processContent(match?.content || ""));
          } else {
            setPage(null);
            setProcessedContent("");
          }
        }
      } catch (err) {
        const error = err?.response?.data?.message || err?.message;
        if (error) {
          Toast(error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPage();
  }, [slug, pages]);

  // Handle link clicks within the dynamic content
  useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement) {
      const handleLinkClick = (e) => {
        const link = e.target.closest('a[href^="/pages/"]');
        if (link) {
          e.preventDefault();
          const href = link.getAttribute('href');
          if (href) {
            navigate(href);
          }
        }
      };

      contentElement.addEventListener('click', handleLinkClick);
      
      return () => {
        if (contentElement) {
          contentElement.removeEventListener('click', handleLinkClick);
        }
      };
    }
  }, [processedContent, navigate]);

  const pageTitle = page?.meta_title || page?.page_name || "Page";
  const pageDescription = page?.meta_description || "";
  const pageKeywords = Array.isArray(page?.meta_keywords)
    ? page.meta_keywords.join(", ")
    : "";

  return (
    <>
      {isLoading && <Loader />}
      <Helmet>
        <title>{pageTitle}</title>
        {pageDescription ? (
          <meta name="description" content={pageDescription} />
        ) : null}
        {pageKeywords ? <meta name="keywords" content={pageKeywords} /> : null}
      </Helmet>
      <div className="container-fluid">
        <div className="container section-padding">
          <div className="row">
            <div className="col-12">
              <h2 className="section-heading text-center text-lg-start">
                {page?.page_name || "Page not found"}
              </h2>
              {processedContent ? (
                <div
                  ref={contentRef}
                  className="dynamic-page-content"
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />
              ) : page?.content ? (
                <div
                  ref={contentRef}
                  className="dynamic-page-content"
                  dangerouslySetInnerHTML={{ __html: page.content }}
                />
              ) : (
                <p className="text-center text-lg-start">
                  The page content is not available.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WebsitePage;
